import { Link, useParams, useSearchParams } from "react-router-dom";
import { useStore } from "@/context/Store";
import { Container, Reveal, useSeo } from "@/components/site/common";
import ProductCard from "@/components/site/ProductCard";

export function ComingSoon({ label }) {
  return (
    <div className="rounded-[2rem] border border-dashed border-[var(--violet)]/40 bg-[var(--lilac)] p-10 text-center" data-testid="coming-soon">
      <p className="font-display text-3xl font-bold">Em breve</p>
      <p className="mx-auto mt-2 max-w-md text-sm text-[var(--muted-ink)]">Ainda estamos a preparar fotografias de {label}. Pede já o teu orçamento e criamos à tua medida.</p>
      <Link to={`/ideia?area=${encodeURIComponent(label)}`} className="btn-primary mt-6" data-testid="coming-soon-quote-button">Pedir orçamento</Link>
    </div>
  );
}

export function CategoryPage() {
  const { cat, sub } = useParams();
  const { categories, products, loading } = useStore();
  const c = categories.find((x) => x.slug === cat);
  const s = c?.subcategories.find((x) => x.slug === sub);
  useSeo(s ? `${s.name} – ${c?.title}` : c?.title, c?.description);
  if (loading) return <Container className="py-24 text-center text-sm">A carregar…</Container>;
  if (!c || (sub && !s)) return <Container className="py-24 text-center"><p className="font-display text-3xl">Página não encontrada</p><Link className="btn-ghost mt-6" to="/">Voltar ao início</Link></Container>;
  const list = products.filter((p) => p.category === c.slug && (!s || p.subcategory === s.slug));
  const subs = s ? [s] : c.subcategories;

  return (
    <div data-testid="category-page">
      <section className="brand-gradient grain text-white">
        <Container className="py-16 sm:py-20">
          <p className="text-sm text-white/80"><Link to="/" className="hover:underline">Início</Link> / <Link to={`/c/${c.slug}`} className="hover:underline">{c.title}</Link>{s && ` / ${s.name}`}</p>
          <h1 className="mt-4 font-display text-5xl font-bold tracking-tight sm:text-6xl" data-testid="category-title">{s ? s.name : c.title}</h1>
          <p className="mt-3 max-w-lg text-white/90">{c.description}</p>
          <div className="mt-8 flex flex-wrap gap-2">
            <Link to={`/c/${c.slug}`} className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${!s ? "bg-white text-[var(--ink)]" : "bg-white/15 hover:bg-white/25"}`}>Tudo</Link>
            {c.subcategories.map((x) => <Link key={x.slug} to={`/c/${c.slug}/${x.slug}`} data-testid={`subcategory-chip-${x.slug}`} className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${s?.slug === x.slug ? "bg-white text-[var(--ink)]" : "bg-white/15 hover:bg-white/25"}`}>{x.name}</Link>)}
          </div>
        </Container>
      </section>
      <Container className="space-y-20 py-16">
        {subs.map((x) => {
          const items = list.filter((p) => p.subcategory === x.slug);
          return (
            <section key={x.slug} data-testid={`subcategory-section-${x.slug}`}>
              {!s && <Reveal className="mb-8 flex items-end justify-between"><h2 className="font-display text-3xl font-bold">{x.name}</h2><Link to={`/c/${c.slug}/${x.slug}`} className="text-sm font-semibold text-[var(--violet)]">Ver →</Link></Reveal>}
              {items.length ? <div className="grid grid-cols-2 gap-x-5 gap-y-10 lg:grid-cols-4">{items.map((p, i) => <ProductCard key={p.id} p={p} i={i} />)}</div> : <ComingSoon label={x.name} />}
            </section>
          );
        })}
      </Container>
    </div>
  );
}

export function SearchPage() {
  const [params] = useSearchParams();
  const q = (params.get("q") || "").trim();
  const { products } = useStore();
  useSeo(`Pesquisa: ${q}`);
  const norm = (t) => (t || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const res = products.filter((p) => norm(`${p.name} ${p.description} ${p.materials}`).includes(norm(q)));
  return (
    <Container className="py-16" >
      <div data-testid="search-page">
        <p className="eyebrow">Pesquisa</p>
        <h1 className="mt-3 font-display text-4xl font-bold sm:text-5xl">“{q}”</h1>
        <p className="mt-2 text-sm text-[var(--muted-ink)]" data-testid="search-results-count">{res.length} resultado(s)</p>
        {res.length ? <div className="mt-10 grid grid-cols-2 gap-x-5 gap-y-10 lg:grid-cols-4">{res.map((p, i) => <ProductCard key={p.id} p={p} i={i} />)}</div>
          : <div className="mt-10"><ComingSoon label={q || "esta pesquisa"} /></div>}
      </div>
    </Container>
  );
}
